import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CalendarDays,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  Sparkles,
  CheckCircle2,
  Inbox,
} from "lucide-react";
import { AfyanexLogo } from "@/components/AfyanexLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHIFT_LABELS = {
  morning: "Morning shift",
  afternoon: "Afternoon shift",
  evening: "Evening shift",
  night: "Night shift",
};

function formatDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

function groupByDate(reports) {
  const map = new Map();
  for (const r of reports) {
    const arr = map.get(r.shift_date) || [];
    arr.push(r);
    map.set(r.shift_date, arr);
  }
  return Array.from(map.entries());
}

export default function FamilyPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/family/${token}`),
      axios.get(`${API}/family-public/${token}/updates`).catch(() => ({ data: [] })),
    ])
      .then(([res, upRes]) => { setData(res.data); setUpdates(upRes.data || []); })
      .catch((err) =>
        setError(err?.response?.data?.detail || "This invitation is no longer available.")
      )
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-bg text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-bg p-6">
        <div className="card-surface max-w-md w-full p-8 text-center" data-testid="family-portal-error">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <h1 className="mt-4 font-heading text-xl font-semibold">Invitation unavailable</h1>
          <p className="mt-2 text-sm text-clinical-subtext">{error}</p>
          <p className="mt-4 text-xs text-clinical-subtext">
            If you believe this is a mistake, please contact the agency for a new invitation link.
          </p>
        </div>
      </div>
    );
  }

  const { client, invite, reports, window_days, caregiver_trust, agency } = data;
  const grouped = groupByDate(reports);
  const trustCaregivers = (caregiver_trust?.caregivers || []).filter(
    (c) => c.total_count > 0
  );
  const branding = agency?.branding || {};
  const agencyName = agency?.name || "Afyanex Care";
  const primary = branding.primary_color || "#004AE6";
  const logoUrl = branding.family_portal_logo_url;

  return (
    <div className="min-h-screen bg-clinical-bg" data-testid="family-portal" style={{ "--brand-primary": primary }}>
      {/* Header — white-labeled per agency */}
      <header className="bg-white border-b border-clinical-border">
        <div className="mx-auto max-w-3xl px-5 py-4 flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt={agencyName} className="h-7 max-w-[160px] object-contain" data-testid="family-agency-logo" />
          ) : (
            <span
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-white font-semibold text-sm"
              style={{ background: primary }}
              aria-hidden="true"
            >
              {agencyName.charAt(0)}
            </span>
          )}
          <span className="font-heading font-semibold text-sm tracking-tight" data-testid="family-agency-name">
            {agencyName}
          </span>
          <span className="ml-1 pl-2.5 border-l border-clinical-border text-[10px] tracking-[0.14em] uppercase text-clinical-subtext font-semibold">
            Family update
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-8 space-y-7">
        {/* Greeting */}
        <section className="card-surface p-6 sm:p-8 animate-fade-up">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-clinical-secondary/25 text-clinical-primary font-heading text-xl font-semibold">
              {client.first_name?.[0] || "C"}
            </div>
            <div className="min-w-0">
              <div className="label-eyebrow">Care updates for</div>
              <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
                {client.name}
              </h1>
              <p className="mt-2 text-sm text-clinical-subtext">
                Hi <span className="text-clinical-text font-medium">{invite.recipient_name}</span>
                {invite.relationship ? ` (${invite.relationship})` : ""}. Here are the most
                recent shift summaries from your loved one's caregivers — last {window_days} days.
              </p>
            </div>
          </div>
        </section>

        {/* Caregiver trust & safety */}
        {trustCaregivers.length > 0 && (
          <section className="card-surface p-5 sm:p-6 animate-fade-up" data-testid="family-trust-section">
            <div className="flex items-start gap-3 mb-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                <ShieldCheck className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="label-eyebrow">Trust & safety</div>
                <h2 className="mt-1 font-heading text-lg font-semibold">
                  Your caregiver{trustCaregivers.length > 1 ? "s are" : " is"} verified
                </h2>
                <p className="mt-1 text-xs text-clinical-subtext">
                  Every Afyanex caregiver is screened by the agency before stepping into your loved one's home.
                </p>
              </div>
              {caregiver_trust?.agency_percent != null && (
                <div className="text-right" data-testid="family-trust-percent">
                  <div className="font-heading text-2xl font-semibold text-emerald-700">
                    {caregiver_trust.agency_percent}%
                  </div>
                  <div className="text-[10px] tracking-[0.12em] uppercase text-clinical-subtext">
                    verified
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-3">
              {trustCaregivers.map((cg, idx) => (
                <div
                  key={`${cg.first_name}-${idx}`}
                  className="rounded-xl border border-clinical-border bg-clinical-bg/40 p-3"
                  data-testid={`family-trust-caregiver-${idx}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-clinical-primary/10 text-clinical-primary font-heading text-xs font-semibold">
                      {cg.first_name?.[0] || "C"}
                    </div>
                    <div className="text-sm font-medium text-clinical-text">{cg.first_name}</div>
                    <div className="ml-auto text-[11px] text-clinical-subtext">
                      {cg.verified_count} of {cg.total_count} checks current
                    </div>
                  </div>
                  {cg.badges.length === 0 ? (
                    <p className="text-[11px] text-clinical-subtext italic">
                      Verification in progress with the agency.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {cg.badges.map((b) => (
                        <span
                          key={`${cg.first_name}-${b.type}`}
                          className="pill bg-emerald-100 text-emerald-800 text-[11px]"
                          data-testid={`family-badge-${b.type}`}
                        >
                          <ShieldCheck className="h-3 w-3" />
                          {b.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Quick caregiver-sent updates feed */}
        {updates.length > 0 && (
          <section className="animate-fade-up" data-testid="family-updates-feed">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-pink-500" />
              <h2 className="font-heading text-base font-medium text-clinical-text">
                Quick updates from your care team
              </h2>
            </div>
            <div className="space-y-2">
              {updates.slice(0, 8).map((u) => (
                <div key={u.id}
                  className="card-surface p-3 sm:p-4"
                  data-testid={`family-update-${u.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs text-clinical-subtext">
                      {u.caregiver_first_name} · {new Date(u.sent_at).toLocaleString(undefined, {
                        weekday: "short", month: "short", day: "numeric",
                        hour: "numeric", minute: "2-digit",
                      })}
                    </span>
                    {u.contains_concern && (
                      <span className="pill bg-amber-100 text-amber-800">Concern</span>
                    )}
                  </div>
                  <p className="text-sm text-clinical-text whitespace-pre-line">{u.message}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Timeline */}
        {reports.length === 0 ? (
          <div className="card-surface p-10 text-center">
            <Inbox className="h-10 w-10 mx-auto text-clinical-subtext" />
            <h3 className="mt-3 font-heading text-lg">No recent updates</h3>
            <p className="mt-1 text-sm text-clinical-subtext">
              The caregiver team has not submitted any reports in the last {window_days} days.
            </p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="family-reports">
            {grouped.map(([day, items]) => (
              <section key={day} className="animate-fade-up">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="h-4 w-4 text-clinical-primary" />
                  <h2 className="font-heading text-base font-medium text-clinical-text">
                    {formatDate(day)}
                  </h2>
                </div>
                <div className="space-y-3">
                  {items.map((r) => (
                    <article
                      key={r.id}
                      className="card-surface p-5 sm:p-6"
                      data-testid={`family-report-${r.id}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="pill bg-clinical-primary/10 text-clinical-primary">
                          {SHIFT_LABELS[r.shift_type] || r.shift_type}
                        </span>
                        <span className="text-xs text-clinical-subtext">
                          with {r.caregiver_first_name}
                        </span>
                      </div>
                      {r.incident_flag && (
                        <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                          <p className="text-xs text-destructive">
                            The caregiver flagged an incident during this shift. The agency has
                            been notified — please reach out to them for details.
                          </p>
                        </div>
                      )}

                      <p className="mt-4 whitespace-pre-wrap leading-relaxed text-clinical-text">
                        {r.notes || (
                          <span className="text-clinical-subtext italic">
                            No additional notes were shared for this shift.
                          </span>
                        )}
                      </p>

                      {r.tasks_completed?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {r.tasks_completed.map((t) => (
                            <span
                              key={t}
                              className="pill bg-clinical-secondary/20 text-clinical-primary capitalize"
                            >
                              <CheckCircle2 className="h-3 w-3" /> {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Footer */}
        <footer className="text-center text-xs text-clinical-subtext pt-4 pb-2">
          <div className="inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            Notes are professionally formatted by Afyanex AI before sharing.
          </div>
          <div className="mt-1">
            This is a private, view-only family link from {agencyName}. Please do not share publicly.
          </div>
          <a
            href="https://www.afyanex.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-clinical-border bg-white px-3 py-1.5 text-[11px] text-clinical-subtext transition-colors hover:border-afyanex-blue/40 hover:text-clinical-text"
            data-testid="powered-by-afyanex"
          >
            <span className="text-clinical-subtext">Powered by</span>
            <AfyanexLogo markSize={14} />
          </a>
        </footer>
      </main>
    </div>
  );
}
